import { type CSSProperties } from "react"
import {
  type PowerLabId,
  type PowerRegionId,
} from "../../../../power-bubble-chart/powerBubbleChartData"

export type LabTransparencyVisualState = "closed" | "dealMembers" | "transparent"

type LabTransparencySchedule = {
  dealMemberStart: number
  transparentStart: number
}

// Whitespace between cells. PowerTreemapCell insets each cell by half of this
// on every interior edge, so this is 2× the whitespace each box gets per side —
// 4 ⇒ 2px of clear space around every box (on top of its border). The
// layout's MIN_CELL_SIDE_PX keeps rects large enough that this inset always
// fits instead of collapsing the box.
export const BLOCK_GAP_PX = 4
export const BLOCK_FILL = "#111111"
// Red highlight for the compute going to frontier AI development (the
// non-borderOnly cells): frontier cells keep the normal fill but take a
// slightly heavier red border and a red logo. Matches the #8b0000 red of the
// canonical scenario figures (e.g. TimelineLineDiagram).
export const FRONTIER_STROKE = "#8b0000"
// Border width (px) of that frontier outline in the default treemap style.
export const FRONTIER_STROKE_WIDTH_PX = 1.5
export const BLOCK_BORDER_RADIUS_PX = 2
export const DEAL_MEMBER_DITHER_SIZE_PX = 5
export const BLOCK_DIMMED_OPACITY = 0.38
// The exact cell under the pointer: slightly lighter than its group's other
// full-strength cells so you can tell which square the hover is reading.
export const BLOCK_HOVERED_CELL_OPACITY = 0.8
export const LOGO_MIN_CELL_SIZE = 12
export const LOGO_MAX_SIZE = 16

const DEAL_MEMBER_DITHER_FILL = BLOCK_FILL
const DEAL_MEMBER_DITHER_DOT_RADIUS_PX = 1.1
const TRANSPARENT_BLOCK_BORDER = "1px dashed #303030"

export const BLOCK_TRANSITION = [
  "left 350ms ease",
  "top 350ms ease",
  "width 350ms ease",
  "height 350ms ease",
  "opacity 160ms ease",
  "background-color 160ms ease",
].join(", ")

const LOGO_OPACITY_BY_STATE: Record<LabTransparencyVisualState, number> = {
  closed: 1,
  dealMembers: 0.7,
  transparent: 0.45,
}

const LOGO_TRANSITION = "opacity 350ms ease"

const DEFAULT_TRANSPARENCY_SCHEDULE: LabTransparencySchedule = {
  dealMemberStart: 0.72,
  transparentStart: 0.96,
}

const labTransparencyScheduleById: Partial<
  Record<PowerLabId, LabTransparencySchedule>
> = {
  openai: { dealMemberStart: 0.42, transparentStart: 0.68 },
  "google-deepmind": { dealMemberStart: 0.45, transparentStart: 0.72 },
  anthropic: { dealMemberStart: 0.48, transparentStart: 0.78 },
  xai: { dealMemberStart: 0.54, transparentStart: 0.84 },
  meta: { dealMemberStart: 0.6, transparentStart: 0.9 },
  alibaba: { dealMemberStart: 0.58, transparentStart: 0.86 },
  bytedance: { dealMemberStart: 0.62, transparentStart: 0.9 },
  humain: { dealMemberStart: 0.64, transparentStart: 0.92 },
  mistral: { dealMemberStart: 0.66, transparentStart: 0.94 },
  deepseek: { dealMemberStart: 0.68, transparentStart: 0.94 },
  cohere: { dealMemberStart: 0.7, transparentStart: 0.96 },
  tencent: { dealMemberStart: 0.72, transparentStart: 0.96 },
}

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value))

const getLabTransparencyProgress = (
  transparencyLevel: number | undefined
): number => {
  const level = transparencyLevel ?? 1
  return clamp01((level - 1) / 4)
}

// Turns the HUD transparency metric into the three visual phases for each lab.
export const getLabTransparencyVisualState = (
  labId: PowerLabId,
  transparencyLevel: number | undefined
): LabTransparencyVisualState => {
  const progress = getLabTransparencyProgress(transparencyLevel)
  const schedule =
    labTransparencyScheduleById[labId] ?? DEFAULT_TRANSPARENCY_SCHEDULE
  if (progress >= schedule.transparentStart) return "transparent"
  if (progress >= schedule.dealMemberStart) return "dealMembers"
  return "closed"
}

// Short label for tooltip column (HUD distribution-of-power tooltip).
export const getLabTransparencyStatusLabel = (
  labId: PowerLabId,
  transparencyLevel: number | undefined
): string => {
  const state = getLabTransparencyVisualState(labId, transparencyLevel)
  if (state === "closed") return "Closed"
  if (state === "dealMembers") return "Partial"
  return "Transparent"
}

const getCellBackgroundImage = (
  state: LabTransparencyVisualState
): string => {
  if (state !== "dealMembers") return "none"
  return [
    "radial-gradient(circle,",
    `${DEAL_MEMBER_DITHER_FILL} 0 ${DEAL_MEMBER_DITHER_DOT_RADIUS_PX}px,`,
    `transparent ${DEAL_MEMBER_DITHER_DOT_RADIUS_PX + 0.1}px`,
    ")",
  ].join(" ")
}

const getCellBackgroundPosition = (
  state: LabTransparencyVisualState,
  x: number,
  y: number
): string | undefined => {
  if (state !== "dealMembers") return undefined
  return `${-(x % DEAL_MEMBER_DITHER_SIZE_PX)}px ${-(y % DEAL_MEMBER_DITHER_SIZE_PX)}px`
}

const getCellBorder = (state: LabTransparencyVisualState): string => (
  state === "transparent" ? TRANSPARENT_BLOCK_BORDER : "none"
)

export const getLogoSize = (width: number, height: number): number => (
  Math.min(LOGO_MAX_SIZE, Math.max(10, Math.min(width, height) * 0.48))
)

export const getLogoStyle = (
  state: LabTransparencyVisualState
): CSSProperties => ({
  opacity: LOGO_OPACITY_BY_STATE[state],
  transition: LOGO_TRANSITION,
})

export type CellLogoTreatment = {
  // When true the logo sits in a white rounded badge with a dark glyph;
  // otherwise the glyph is drawn directly in `color`.
  onWhiteBadge: boolean
  color: string
}

// Which country group a cell belongs to, and whether that group currently
// holds the most compute. Lets region-ranked treatments (e.g. the fill/outline
// lower-HUD variant) style the leading country differently from the rest.
export type TreemapCellRegionInfo = {
  regionId: PowerRegionId
  isTopRegion: boolean
  // True while the pointer is over this cell's country group (any of its
  // cells). Hover-aware treatments (e.g. the red-outline variant) read this;
  // the default treatments ignore it.
  isInHoveredRegion?: boolean
  // True only for the exact cell under the pointer. Single-company hover
  // treatments (hoverScope "cell" in the fade style) read this instead of
  // isInHoveredRegion.
  isHoveredCell?: boolean
  // True when this cell is compute used for frontier AI development (a filled
  // lab, not the borderOnly "Rest of X" residual). Highlight treatments read
  // it to draw the red border / red logo.
  isFrontier?: boolean
}

export type GetCellLogoTreatment = (
  state: LabTransparencyVisualState,
  regionInfo?: TreemapCellRegionInfo
) => CellLogoTreatment

// Default: white glyph on the solid black square, dark glyph on a white badge
// once the square is no longer solid.
export const getCellLogoTreatment: GetCellLogoTreatment = state => {
  const onWhiteBadge = state !== "closed"
  return { onWhiteBadge, color: onWhiteBadge ? "#111111" : "#ffffff" }
}

export type TreemapCellStyleArgs = {
  left: number
  top: number
  width: number
  height: number
  opacity: number
  state: LabTransparencyVisualState
  // Country group of this cell, and whether it leads on compute. Region-ranked
  // treatments read these; the default/fade/dither styles ignore them.
  regionId: PowerRegionId
  isTopRegion: boolean
  // 0-based rank of this cell's country group by total compute (0 = most
  // compute, 1 = second, …). Lets a treatment vary by rank — e.g. the
  // per-group dither lower-HUD gives the leading country the densest fill.
  regionRank: number
  // See TreemapCellRegionInfo.isInHoveredRegion.
  isInHoveredRegion?: boolean
  // True only for the exact cell under the pointer — its region siblings get
  // isInHoveredRegion instead. Treatments that override `opacity` (e.g. the
  // fade style's red-fill hover) should render this cell at
  // BLOCK_HOVERED_CELL_OPACITY so the pointer's own square reads lighter.
  isHoveredCell?: boolean
  // See TreemapCellRegionInfo.isFrontier — true for frontier-AI-development
  // compute, false for the borderOnly "Rest of X" residual.
  isFrontier?: boolean
}

export type GetTreemapCellStyle = (args: TreemapCellStyleArgs) => CSSProperties

export const getTreemapCellStyle: GetTreemapCellStyle = ({
  left,
  top,
  width,
  height,
  opacity,
  state,
  isFrontier,
}): CSSProperties => ({
  left,
  top,
  width,
  height,
  backgroundColor: state === "closed" ? BLOCK_FILL : "transparent",
  backgroundImage: getCellBackgroundImage(state),
  backgroundPosition: getCellBackgroundPosition(state, left, top),
  backgroundSize:
    state === "dealMembers"
    ? `${DEAL_MEMBER_DITHER_SIZE_PX}px ${DEAL_MEMBER_DITHER_SIZE_PX}px`
      : undefined,
  // Frontier cells keep their fill but take a red outline.
  border: isFrontier
    ? `${FRONTIER_STROKE_WIDTH_PX}px solid ${FRONTIER_STROKE}`
    : getCellBorder(state),
  borderRadius: BLOCK_BORDER_RADIUS_PX,
  boxSizing: "border-box",
  opacity,
  transition: BLOCK_TRANSITION,
})
